import React from 'react';
import { Route, Routes, Navigate, useParams } from 'react-router-dom';
import LoginPage from './LoginPage';
import WorkosCallbackPage from './WorkosCallbackPage';
import Dashboard from './Dashboard';
import HOAPage from './HOAPage';
import HOADetailPage from './HOADetailPage';
import HomeDetailPage from './HomeDetailPage';
import ViolationPage from './ViolationPage';
import ViolationList from './ViolationList';
import ViolationDetailPage from './ViolationDetailPage';
import ProtectedRoute from './ProtectedRoute';
import SettingsPage from './SettingsPage';
import UserManagementPage from './UserManagementPage';
import { ThemeProvider } from './common/Custom/ThemeProvider';
import MemberDetailPage from './MemberDetailPage';
import DevLoginPage from './DevLoginPage';
import NotificationPage from './NotificationPage';
import NotificationDetailPage from './NotificationDetailPage';
import CommunicationPage from './CommunicationPage';
import CommunicationDetailPage from './CommunicationDetailPage';
import WorkOrderPage from './WorkOrderPage';
import WorkOrderDetailPage from './WorkOrderDetailPage';
import VendorPage from './VendorPage';
import VendorDetailPage from './VendorDetailPage';
import BankingPage from './BankingPage';
import AccountingPage from './AccountingPage';
import BillingPage from './BillingPage';
import { UserProvider } from './hooks/UserContext';
import { CommandPaletteProvider } from './shell/CommandPaletteProvider';
import { SyncHold, SyncProvider } from './sync/SyncProvider';
import { HeldShell } from './shell/HeldShell';
import HomeBillingPage from './HomeBillingPage';
import ReservationsPage from './ReservationsPage';
import ReservationDetailPage from './ReservationDetailPage';
import ReservationSeriesPage from './ReservationSeriesPage';
import ReservationUtilizationPage from './ReservationUtilizationPage';
import ReservationRevenuePage from './ReservationRevenuePage';
import ReservationSuspensionsPage from './ReservationSuspensionsPage';
import AmenitiesPage from './AmenitiesPage';
import AmenityDetailPage from './AmenityDetailPage';
import NotFoundPage from './NotFoundPage';

/**
 * `ViolationList` scoped by the URL it was routed to.
 *
 * These routes used to render a bare `<ViolationList />`, which ignored
 * both params and listed every violation the caller could see under a
 * heading that promised one HOA.
 */
const RoutedViolationList: React.FC = () => {
  const { hoaId, homeId } = useParams<{ hoaId: string; homeId?: string }>();
  return <ViolationList hoaId={hoaId} homeId={homeId} />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <UserProvider>
        {/* The local copy every synced view reads (#561), then one ⌘K
            palette for the whole signed-in app (#603). */}
        <SyncProvider>
        <CommandPaletteProvider>
        {/* Waiting for that copy, the signed-in shell stays on screen
            and only the page waits (#667). */}
        <SyncHold fallback={<HeldShell />}>
        <div className="min-h-screen bg-stone-300 dark:bg-gray-900">
          <Routes>
            {/* Authentication Routes.
                `/forgot-password` and `/reset-password` were removed
                2026-09-12: AuthKit owns password recovery, and the two
                endpoints they posted to are now a SuperAdmin
                break-glass reachable only with a tool. A stale link to
                either falls through to `*` below. */}
            <Route path="/login" element={<LoginPage />} />
            {/*
              Password sign-in for environments AuthKit cannot serve — localhost
              and PR namespaces, whose redirect URIs WorkOS was never told about.
              **Deliberately not linked from `/login`**, which stays a door
              rather than a form; you reach this by typing the URL.
              The page renders no form unless `GET /auth/dev-login` answers,
              and that route is registered only below `prod`.
            */}
            <Route path="/dev-login" element={<DevLoginPage />} />
            <Route path="/callback" element={<WorkosCallbackPage />} />

            {/* Protected Routes: one guard for all of them (#635), so moving
                between pages never remounts it. */}
            <Route element={<ProtectedRoute />}>
              <Route
                path="/home"
                element={<Dashboard currentPage="home" />}
              />
              <Route
                path="/hoas"
                element={<HOAPage currentPage="hoas" />}
              />
              <Route
                path="/settings"
                element={<SettingsPage currentPage="settings" />}
              />
              {/*
                Guarded by `ProtectedRoute` like everything else, and then
                again by the backend, which checks the row rather than the
                claim. The page itself decides only which of its two
                controls to render — a Manager who typed this URL would see
                the shell and an empty list, because `listUsers` answers 403
                for them.
              */}
              <Route
                path="/users"
                element={<UserManagementPage currentPage="users" />}
              />
              <Route
                path="/violations"
                element={<ViolationPage currentPage="violations" />}
              />
              <Route
                path="/hoa/:hoaId/violations"
                element={<RoutedViolationList />}
              />
              <Route
                path="/hoa/:hoaId/home/:homeId/violations"
                element={<RoutedViolationList />}
              />
              <Route
                path="/violations/:violationId"
                element={<ViolationDetailPage />}
              />
              <Route
                path="/hoa/:id"
                element={<HOADetailPage />}
              />
              <Route
                path="/hoa/:hoaId/member/:memberId"
                element={<MemberDetailPage />}
              />
              <Route
                path="/hoa/:hoaId/home/:homeId"
                element={<HomeDetailPage />}
              />
              <Route
                path="/notifications"
                element={<NotificationPage currentPage="notifications" />}
              />
              <Route
                path="/notifications/:notificationId"
                element={<NotificationDetailPage />}
              />
              <Route
                path="/communications"
                element={<CommunicationPage currentPage="communications" />}
              />
              <Route
                path="/communications/:communicationId"
                element={<CommunicationDetailPage />}
              />
              <Route
                path="/work-orders"
                element={<WorkOrderPage currentPage="work-orders" />}
              />
              <Route
                path="/work-orders/:workOrderId"
                element={<WorkOrderDetailPage />}
              />
              <Route
                path="/hoa/:hoaId/work-orders"
                element={<WorkOrderPage currentPage="work-orders" />}
              />
              <Route
                path="/hoa/:hoaId/home/:homeId/work-orders"
                element={<WorkOrderPage currentPage="work-orders" />}
              />
              <Route
                path="/vendors"
                element={<VendorPage currentPage="vendors" />}
              />
              <Route
                path="/vendors/:vendorId"
                element={<VendorDetailPage />}
              />
              <Route
                path="/hoa/:hoaId/vendors"
                element={<VendorPage currentPage="vendors" />}
              />
              <Route
                path="/banking"
                element={<BankingPage currentPage="banking" />}
              />

              {/* Amenity reservations (#642): the approval queue and
                  calendar, one booking, and an association's amenities.
                  `/reservations/:id` is what a notification links to. */}
              <Route path="/reservations" element={<ReservationsPage />} />
              <Route
                path="/reservations/:reservationId"
                element={<ReservationDetailPage />}
              />
              <Route
                path="/hoa/:hoaId/reservations"
                element={<ReservationsPage />}
              />
              <Route
                path="/hoa/:hoaId/reservations/:reservationId"
                element={<ReservationDetailPage />}
              />
              {/* Phase 2: a recurring series, and the utilization report.
                  Static segments outrank `:reservationId`. */}
              <Route
                path="/hoa/:hoaId/reservations/series/:seriesId"
                element={<ReservationSeriesPage />}
              />
              <Route
                path="/hoa/:hoaId/reservations/utilization"
                element={<ReservationUtilizationPage />}
              />
              {/* Phase 3: the revenue report and booking suspensions. */}
              <Route
                path="/hoa/:hoaId/reservations/revenue"
                element={<ReservationRevenuePage />}
              />
              <Route
                path="/hoa/:hoaId/reservations/suspensions"
                element={<ReservationSuspensionsPage />}
              />
              <Route
                path="/hoa/:hoaId/home/:homeId/reservations"
                element={<ReservationsPage />}
              />
              <Route
                path="/hoa/:hoaId/amenities"
                element={<AmenitiesPage />}
              />
              <Route
                path="/hoa/:hoaId/amenities/:amenityId"
                element={<AmenityDetailPage />}
              />

              {/* Billing — per-HOA policy and process. The home's own
                  account is the block below; see docs/DUES_FRONTEND_PLAN.md
                  for why the two are separate. */}
              <Route
                path="/hoa/:hoaId/billing"
                element={<BillingPage section="overview" />}
              />
              <Route
                path="/hoa/:hoaId/billing/settings"
                element={<BillingPage section="settings" />}
              />
              <Route
                path="/hoa/:hoaId/billing/dues"
                element={<BillingPage section="dues" />}
              />
              <Route
                path="/hoa/:hoaId/billing/runs"
                element={<BillingPage section="runs" />}
              />
              <Route
                path="/hoa/:hoaId/billing/runs/:runId"
                element={<BillingPage section="runs" />}
              />
              <Route
                path="/hoa/:hoaId/billing/payments"
                element={<BillingPage section="payments" />}
              />
              <Route
                path="/hoa/:hoaId/billing/collections"
                element={<BillingPage section="collections" />}
              />
              <Route
                path="/hoa/:hoaId/billing/collections/:caseId"
                element={<BillingPage section="collections" />}
              />

              {/* Accounting — the association's own books. A sibling of
                  billing, not a tab inside it: billing is a homeowner's
                  account and this is hoa x fund x account. See
                  docs/GL_FRONTEND_PLAN.md. */}
              <Route
                path="/hoa/:hoaId/accounting"
                element={<AccountingPage section="close" />}
              />
              <Route
                path="/hoa/:hoaId/accounting/reconciliations"
                element={<AccountingPage section="reconciliations" />}
              />
              <Route
                path="/hoa/:hoaId/accounting/reconciliations/:recId"
                element={<AccountingPage section="reconciliations" />}
              />
              <Route
                path="/hoa/:hoaId/accounting/reports"
                element={<AccountingPage section="reports" />}
              />
              <Route
                path="/hoa/:hoaId/accounting/chart"
                element={<AccountingPage section="chart" />}
              />
              <Route
                path="/hoa/:hoaId/accounting/budgets"
                element={<AccountingPage section="budgets" />}
              />

              {/* Billing — a home's account. */}
              <Route
                path="/hoa/:hoaId/home/:homeId/billing"
                element={<HomeBillingPage section="account" />}
              />
              <Route
                path="/hoa/:hoaId/home/:homeId/billing/statement"
                element={<HomeBillingPage section="statement" />}
              />
              <Route
                path="/hoa/:hoaId/home/:homeId/billing/payments"
                element={<HomeBillingPage section="payments" />}
              />
              <Route
                path="/hoa/:hoaId/home/:homeId/billing/tenures"
                element={<HomeBillingPage section="tenures" />}
              />
              <Route
                path="/hoa/:hoaId/home/:homeId/billing/estoppel"
                element={<HomeBillingPage section="estoppel" />}
              />

              {/* An unknown URL is a 404 once signed in (#652). Inside the
                  guard, so a signed-out visitor still lands on /login. */}
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </div>
        </SyncHold>
        </CommandPaletteProvider>
        </SyncProvider>
      </UserProvider>
    </ThemeProvider>
  );
};

export default App;
